import type { Incident } from '../../incidents/types/incident.types'
import { formatClockTime } from '../utils/liveOpsAggregate'

interface CriticalAlertsProps {
  alerts: Incident[]
  onAck: (id: number) => void
  isUpdating: boolean
}

interface AlertCardProps {
  alert: Incident
  onAck?: (id: number) => void
  isUpdating: boolean
}

function AlertCard({ alert, onAck, isUpdating }: AlertCardProps): JSX.Element {
  return (
    <div className="mb-3 rounded-lg border border-[#E5E7EB] bg-[#FEF2F2] p-3 last:mb-0">
      <p className="mb-0.5 text-[11px] font-bold uppercase tracking-wider text-red-600">
        {alert.priority}
      </p>
      <p className="mb-1 text-[14px] font-bold text-gray-900">{alert.title}</p>
      {alert.description && (
        <p className="mb-1 text-[12px] text-gray-600">{alert.description}</p>
      )}
      <p className={`text-[11px] text-gray-500 ${onAck ? 'mb-3' : ''}`}>
        {(alert.location ?? 'Unknown location')} · {formatClockTime(alert.created_at)}
      </p>
      {onAck && (
        <button
          type="button"
          disabled={isUpdating}
          onClick={() => onAck(alert.id)}
          className="rounded-md bg-white px-3 py-1.5 text-[12px] font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          ACK
        </button>
      )}
    </div>
  )
}

export function CriticalAlerts({ alerts, onAck, isUpdating }: CriticalAlertsProps): JSX.Element {
  const newAlerts = alerts.filter((a) => a.status === 'NEW')
  const acknowledgedAlerts = alerts.filter((a) => a.status === 'ACKNOWLEDGE' || a.status === 'IN_PROGRESS')

  return (
    <div className="flex w-full flex-shrink-0 flex-col rounded-xl border border-[#E5E7EB] bg-white lg:w-72">
      <div className="border-b border-[#E5E7EB] p-4">
        <h2 className="text-[18px] font-bold text-gray-900">Critical Alerts</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {alerts.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No critical alerts</p>
        ) : (
          <>
            {newAlerts.length > 0 && (
              <section className="mb-4 last:mb-0">
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">New</h3>
                {newAlerts.map((alert) => (
                  <AlertCard key={alert.id} alert={alert} onAck={onAck} isUpdating={isUpdating} />
                ))}
              </section>
            )}
            {acknowledgedAlerts.length > 0 && (
              <section className="mb-4 last:mb-0">
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">Acknowledged</h3>
                {acknowledgedAlerts.map((alert) => (
                  <AlertCard key={alert.id} alert={alert} isUpdating={isUpdating} />
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  )
}
